// @flow
import * as React from 'react';
import ContactCell from 'app/common/react/components/BoardCollab/ContactCell';
import i18n from 'app/common/lib/i18n';
import type { AuthViewer } from 'app/packages/pinterest-flow-types/viewer';
import withViewer from 'app/common/lib/react/withViewer';
import { compose } from 'redux';
import { dangerouslyFormat } from 'app/common/lib/StringUtil';
import { type Invite } from 'app/common/react/components/BoardCollab/types';
import { Box, Button } from 'gestalt';

type Props = {|
  invite: Invite,
  isDim?: boolean,
  viewer: AuthViewer,
  actionsDisabled: boolean,
  onApprove?: () => void,
  onDelete?: () => void,
|};

/*
 * BoardCollabInviteCell is a presentation component that represents a confirmed or pending
 * board collaborator/invite. If actions are available, buttons are included.
 */
class BoardCollabInviteCell extends React.Component<Props> {
  getSubtitleText(
    isViewerInvitedUser: boolean,
    isViewerInvitedByUser: boolean,
    isRequestToJoin: boolean,
  ): string {
    const invitedByUserFullName = this.props.invite.invited_by_user.full_name;

    if (isViewerInvitedUser) {
      return i18n._("That's you", 'subtext for current user who requested to join this board');
    }

    if (isViewerInvitedByUser) {
      return i18n._(
        'Invited by you',
        'subtext for a pending collaborator who was invited by the current viewer',
      );
    }

    if (isRequestToJoin) {
      return i18n._(
        'Requested to join',
        'The subtext that identifies a pending collaborator has personally requested to join the board',
      );
    }

    return dangerouslyFormat(
      i18n._(
        'Invited by {{ user_full_name }}',
        'The subtext that identifies a pending collaborator who was invited to collaborate on the board',
      ),
      { user_full_name: invitedByUserFullName },
    );
  }

  getDeclineDeleteText(
    isViewerInvitedUser: boolean,
    isRequestToJoin: boolean,
    accepted: boolean,
  ): string {
    if (isViewerInvitedUser) {
      return i18n._('Leave', '[button] for a collaborator to leave a group board');
    }

    if (isRequestToJoin && !accepted) {
      return i18n._(
        'Decline',
        "[button] decline another pinner's request to join board as a collaborator",
      );
    }

    return i18n._('Remove', '[button] remove a collaborator from the board]');
  }

  render() {
    const {
      onApprove,
      isDim,
      onDelete,
      actionsDisabled,
      viewer,
      invite: { access, status, invited_by_user: invitedByUser, invited_user: invitedUser },
    } = this.props;
    const isViewerInvitedUser = invitedUser.id === viewer.id;
    const isViewerInvitedByUser = invitedByUser.id === viewer.id;
    const isRequestToJoin = invitedUser.id === invitedByUser.id;
    const accepted = status === 'accepted';

    const canApprove = access.includes('approve');
    const canDelete = access.includes('delete');

    const approveButtonText = i18n._(
      'Approve',
      "[button] approve another pinner's request to join the board as a collaborator",
    );

    const deleteButtonText = this.getDeclineDeleteText(
      isViewerInvitedUser,
      isRequestToJoin,
      accepted,
    );

    const showInlineDelete = canDelete && !canApprove;
    const showApproveAndDelete = canDelete && canApprove;
    const invitedUserProfileLink = `/${invitedUser.username}/`;

    return (
      <Box>
        <ContactCell
          imageUrl={invitedUser.image_medium_url || ''}
          name={invitedUser.full_name}
          title={invitedUser.full_name}
          subtitle={this.getSubtitleText(
            isViewerInvitedUser,
            isViewerInvitedByUser,
            isRequestToJoin,
          )}
          linkURL={invitedUserProfileLink}
          isDim={isDim !== undefined ? isDim : !accepted}
          accessory={
            showInlineDelete && (
              <Button
                inline
                disabled={actionsDisabled}
                onClick={onDelete}
                text={deleteButtonText}
                size="sm"
              />
            )
          }
        />
        {showApproveAndDelete && (
          <Box paddingX={3} marginLeft={12} display="flex" justifyContent="end">
            <Box paddingX={1} flex="grow" width="50%">
              <Button
                disabled={actionsDisabled}
                onClick={onDelete}
                text={deleteButtonText}
                size="sm"
              />
            </Box>
            <Box paddingX={1} flex="grow" width="50%">
              <Button
                color="red"
                disabled={actionsDisabled}
                onClick={onApprove}
                text={approveButtonText}
                size="sm"
              />
            </Box>
          </Box>
        )}
      </Box>
    );
  }
}

export default compose(withViewer)(BoardCollabInviteCell);
